import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  useYouTubeAuthStatus,
  useYouTubeAuth,
  useYouTubeAuthListener,
} from '../../youtube/hooks';
import type { YouTubePrivacyStatus } from '../../../services/asyncJobApi';
import { useToast } from '../../../hooks/useToast';
import { HelpTooltip } from '../../../components/common/HelpTooltip';
import '../../../styles/badges.css';
import styles from './YouTubeUploadModal.module.css';

export interface YouTubeUploadMetadata {
  title: string;
  description: string;
  privacy: YouTubePrivacyStatus;
  containsSyntheticMedia: boolean;
}

/** アップロード対象 (シナリオ / 単体素材)。フォーム初期値と pending キーに使う。 */
export interface YouTubeUploadSource {
  id: string;
  defaultTitle: string;
  defaultDescription: string;
}

interface YouTubeUploadModalProps {
  /** null の間はモーダル非表示。値が入ると開く */
  source: YouTubeUploadSource | null;
  onClose: () => void;
  /** メタデータ確定。アップロード開始は親が行い、モーダルはこの後すぐ閉じる。 */
  onSubmit: (metadata: YouTubeUploadMetadata) => void;
  /**
   * ポップアップがブロックされ同タブ遷移で連携した場合に、戻り先で自動的に
   * このモーダルを開き直せるか。シナリオ画面 (ScenarioGenerator) は対応するため
   * true。素材プレビュー等の再オープン未対応の呼び出し元は false にし、
   * その旨を連携プロンプトに反映する。既定 true。
   */
  reopenAfterAuth?: boolean;
}

const PRIVACY_OPTIONS: { value: YouTubePrivacyStatus; label: string; hint: string; icon: string }[] = [
  { value: 'private', label: '非公開', hint: '自分だけが視聴可能。後からYouTube Studioで公開できます。', icon: '/lock.svg' },
  { value: 'unlisted', label: '限定公開', hint: 'URLを知っている人だけ視聴可能', icon: '/link_chain.svg' },
  { value: 'public', label: '公開', hint: '誰でも検索・視聴可能', icon: '/network_users.svg' },
];

// 公開設定の最後の選択を localStorage に保存し、次回のデフォルトにする。
const PRIVACY_STORAGE_KEY = 'youtube_privacy_last';

function readStoredPrivacy(): YouTubePrivacyStatus {
  try {
    const v = localStorage.getItem(PRIVACY_STORAGE_KEY);
    if (v === 'private' || v === 'unlisted' || v === 'public') return v;
  } catch {
    /* noop */
  }
  return 'private';
}

export function YouTubeUploadModal({ source, onClose, onSubmit, reopenAfterAuth = true }: YouTubeUploadModalProps) {
  const toast = useToast();
  const { data: status, isLoading: isStatusLoading } = useYouTubeAuthStatus();
  const { startAuth, isAuthenticating } = useYouTubeAuth();
  // ポップアップで連携できた場合はこのタブが生存しているので pending マーカーは不要。
  // (同タブ遷移時はこのリスナーは発火せず、親側が pending を消費する)
  useYouTubeAuthListener(() => {
    try {
      sessionStorage.removeItem('youtube_pending_scenario');
    } catch {
      /* noop */
    }
  });

  // モーダルを明示的に閉じたら、保留中の再オープン意図 (pending) も破棄する。
  const handleClose = () => {
    try {
      sessionStorage.removeItem('youtube_pending_scenario');
    } catch {
      /* noop */
    }
    onClose();
  };

  // 連携開始: 同タブ遷移にフォールバックした場合に、戻ってきた後この動画の
  // アップロードモーダルを開き直せるよう、対象シナリオIDを控えてから認証へ。
  const handleStartAuth = () => {
    try {
      // 再オープン対応の呼び出し元のみ pending を保存する (素材側は消費者が無いため保存しない)。
      if (source && reopenAfterAuth) {
        sessionStorage.setItem('youtube_pending_scenario', source.id);
      }
    } catch {
      /* noop */
    }
    void startAuth();
  };

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [privacy, setPrivacy] = useState<YouTubePrivacyStatus>(readStoredPrivacy);
  // ホバー中のオプション。説明文は (ホバー中 ?? 選択中) のものを表示する。
  const [hoveredPrivacy, setHoveredPrivacy] = useState<YouTubePrivacyStatus | null>(null);
  // 改変/合成コンテンツ (AI) の自己申告。デフォルト Off。
  const [containsSyntheticMedia, setContainsSyntheticMedia] = useState(false);

  // 選択を localStorage に保存して次回のデフォルトにする。
  const handleSelectPrivacy = (value: YouTubePrivacyStatus) => {
    setPrivacy(value);
    try {
      localStorage.setItem(PRIVACY_STORAGE_KEY, value);
    } catch {
      /* noop */
    }
  };

  // 対象が変わるたびにフォームを初期化
  useEffect(() => {
    if (source) {
      setTitle(source.defaultTitle || '');
      setDescription(source.defaultDescription || '');
      setPrivacy(readStoredPrivacy());
      setContainsSyntheticMedia(false);
    }
  }, [source?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!source) return null;

  const connected = status?.connected === true;

  const handleSubmit = () => {
    if (!title.trim()) {
      toast.error('タイトルを入力してください');
      return;
    }
    // AI申告を On のまま実行する場合は、誤申告を防ぐため確認する。
    if (containsSyntheticMedia) {
      const ok = window.confirm(
        'これは「AIで実在の人物・出来事・場所をリアルに改変/合成した」ことの自己申告フラグ（ディープフェイク・虚偽動画の開示用）です。\n\n' +
        '該当する動画であれば申告した方がチャンネルを守れますが、該当しない動画に付けると不要な「変更されたコンテンツ」ラベルが表示されます。\n\n' +
        'このまま申告して続行しますか？'
      );
      if (!ok) return;
    }
    // メタデータを親へ渡してモーダルは閉じる (アップロード中は他操作をブロックしない)。
    onSubmit({ title: title.trim(), description, privacy, containsSyntheticMedia });
    handleClose();
  };

  return createPortal(
    // 入力途中の誤離脱を防ぐため、背景クリック (モーダル内で押して外で離すドラッグ含む) では閉じない。
    // 閉じるのは × / キャンセルのみ。stopPropagation は Portal 越しに親 (素材プレビュー等) の
    // 背景クリックハンドラへ伝播して親ごと閉じるのを防ぐため残す。
    <div className={styles.backdrop} onClick={(e) => e.stopPropagation()}>
      <div className={styles.modal}>
        <div className={styles.header}>
          <img src="/youtube_logo.svg" alt="" className={styles.titleIcon} />
          <span className={styles.title}>YouTubeにアップロード</span>
        </div>
        <button type="button" onClick={handleClose} className={styles.closeButton} aria-label="閉じる">×</button>

        {isStatusLoading ? (
          <p className={styles.muted}>連携状態を確認中...</p>
        ) : !connected ? (
          <div className={styles.body}>
            <p>YouTubeへアップロードするには、ご自身のYouTubeアカウントとの連携が必要です。</p>
            <div className={styles.actions} style={{ justifyContent: 'flex-start' }}>
              <button
                type="button"
                className="btn-modal"
                onClick={handleStartAuth}
                disabled={isAuthenticating}
              >
                {isAuthenticating ? '連携中...' : 'YouTubeと連携する'}
              </button>
            </div>
            <p className={styles.muted}>
              {reopenAfterAuth
                ? '連携が完了するとこの画面に戻り、フォームが表示されます。'
                : '連携が完了したら、もう一度この動画からアップロードしてください。'}
            </p>
          </div>
        ) : (
          <div className={styles.body}>
            {status?.channel_title && (
              <p className={styles.muted}>
                連携中のチャンネル: <code className="code-value">{status.channel_title}</code>
              </p>
            )}
            <label className="form-label-common">
              <span>タイトル</span>
              <input
                type="text"
                value={title}
                maxLength={100}
                onChange={(e) => setTitle(e.target.value)}
              />
            </label>
            <label className="form-label-common">
              <span>説明</span>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={4}
              />
            </label>
            <div className="form-label-common">
              <span>公開設定</span>
              <div className="chip-button-group" role="radiogroup" aria-label="公開設定">
                {PRIVACY_OPTIONS.map((o) => (
                  <button
                    key={o.value}
                    type="button"
                    role="radio"
                    aria-checked={privacy === o.value}
                    className={`chip-button ${privacy === o.value ? 'chip-button-active' : ''}`}
                    onClick={() => handleSelectPrivacy(o.value)}
                    onMouseEnter={() => setHoveredPrivacy(o.value)}
                    onMouseLeave={() => setHoveredPrivacy((h) => (h === o.value ? null : h))}
                  >
                    <img src={o.icon} alt="" className={styles.chipIcon} />
                    {o.label}
                  </button>
                ))}
              </div>
            </div>
            <p className={styles.hint}>
              {PRIVACY_OPTIONS.find((o) => o.value === (hoveredPrivacy ?? privacy))?.hint}
            </p>

            <label className="form-checkbox-label">
              <input
                type="checkbox"
                checked={containsSyntheticMedia}
                onChange={(e) => setContainsSyntheticMedia(e.target.checked)}
              />
              <span className={styles.aiLabelText}>
                この動画は
                <a
                  href="https://support.google.com/youtube/answer/14328491?hl=ja"
                  target="_blank"
                  rel="noopener noreferrer"
                  className={styles.aiLabelLink}
                  onClick={(e) => e.stopPropagation()}
                >特定の目的</a>
                でAIを利用しました
              </span>
              <HelpTooltip ariaLabel="AI利用の申告に関するヘルプ">
                以下の目的でコンテンツの生成・編集にAIを使用した場合に申告します:
                <br />・実在の人物が実際にはしていない発言や行動をしているように見せている
                <br />・実際の出来事や場所の映像を改変している
                <br />・実際には起きていない場面を現実のように見せるコンテンツを生成している
              </HelpTooltip>
            </label>

            <div className={styles.actionsSplit}>
              <button type="button" className="btn-cancel" onClick={handleClose}>キャンセル</button>
              <button type="button" className="btn-modal" onClick={handleSubmit}>アップロード</button>
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}
